import { ChevronRightIcon } from "@heroicons/react/24/outline";
import { convertReadableString } from "@utils/strings";
import { BasecampRole } from "@utils/types";
import { useState } from "react";

import { Button, Dialog, Heading, Paragraph, type SelectItem, Tooltip } from "@fronterahealth/frontera-ui-components";

import {
  ConnectionTypeEnum,
  type CreateOrganizationUserInput,
  type GetOrganizationUsersOutput,
  type GetOrganizationsOutput,
  type GetOrphanedOrganizationUsersOutput,
  type RemoveOrganizationUserInput,
  type RestoreOrphanedOrganizationUserInput,
  RolesEnum,
  type UpdateOrganizationUserInput,
  useBasecampOrganizationUsersCreateCreate,
  useBasecampOrganizationUsersRemoveCreate,
  useBasecampOrganizationUsersRestoreCreate,
  useBasecampOrganizationUsersUpdateCreate,
} from "@api/rest/types-and-hooks";
import { useFormUtils } from "@components/forms/useFormUtils";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import { useBasecampRoles } from "@hooks/useBasecampRoles";
import { ProviderProfileFields } from "@pages/Organizations/ProviderProfileFields";
import { type ProviderProfileValues, changedProviderProfileFields } from "@pages/Organizations/providerProfile";

const CONNECTION_TYPE_ITEMS: SelectItem[] = Object.entries(ConnectionTypeEnum).map(([, value]) => ({
  id: value,
  primary: convertReadableString(value),
}));

enum FormMode {
  Create = "create",
  Edit = "edit",
  Restore = "restore",
}

const getFormMode = (isEditMode: boolean, isRestoreMode: boolean): FormMode => {
  if (isEditMode) return FormMode.Edit;
  if (isRestoreMode) return FormMode.Restore;
  return FormMode.Create;
};

const MODE_MESSAGES: Record<FormMode, { success: string; failure: string }> = {
  [FormMode.Edit]: { success: "User updated successfully", failure: "Failed to update user" },
  [FormMode.Restore]: { success: "User restored successfully", failure: "Failed to restore user" },
  [FormMode.Create]: { success: "User created successfully", failure: "Failed to create user" },
};

const SUBMIT_LABELS: Record<FormMode, { idle: string; submitting: string }> = {
  [FormMode.Edit]: { idle: "Update", submitting: "Updating..." },
  [FormMode.Restore]: { idle: "Restore access", submitting: "Restoring..." },
  [FormMode.Create]: { idle: "Submit", submitting: "Submitting..." },
};

const getTitle = (
  mode: FormMode,
  user: GetOrganizationUsersOutput | undefined,
  restoreTarget: GetOrphanedOrganizationUsersOutput | undefined,
): string => {
  switch (mode) {
    case FormMode.Edit:
      return user?.full_name ?? "Edit user";
    case FormMode.Restore:
      return `Restore access for ${restoreTarget?.full_name ?? restoreTarget?.email ?? "user"}`;
    case FormMode.Create:
      return "Create new user";
  }
};

const ROLES: { id: RolesEnum; label: string; tooltip: string }[] = [
  {
    id: RolesEnum.admin,
    label: "Admin",
    tooltip:
      "Admins can access client and provider profiles, review assessments/diagnostics reports, and manage provider/client profiles. Admins also have access to the video portal, allowing them to review session footage across the organization.",
  },
  {
    id: RolesEnum["assessment-bcba"],
    label: "Assessment BCBA",
    tooltip:
      "Assessment BCBAs can view all assessments across the clinic and access assessment templates specific to their role. This role is focused on performing and managing all aspects of assessments.",
  },
  {
    id: RolesEnum.bcba,
    label: "BCBA",
    tooltip:
      "BCBAs can view assessment reports and templates for clients they are assigned to. They also have access to the video portal to review session footage submitted by RBTs for their clients.",
  },
  {
    id: RolesEnum.caregiver,
    label: "Caregiver",
    tooltip: "These are going to be logins of families on behalf of their child.",
  },
  {
    id: RolesEnum.director,
    label: "Director",
    tooltip: "These are going to be the clinical directors at a company that oversee the whole operation.",
  },
  {
    id: RolesEnum["frontera-internal"],
    label: "Frontera Employee",
    tooltip: "These are going to be Frontera Employees with a '@frontera.health' email.",
  },
  {
    id: RolesEnum.rbt,
    label: "RBT",
    tooltip:
      "RBTs can record and upload session videos for their assigned clients. They can review their own uploads in the video portal but do not have access to assessments or other RBTs’ videos.",
  },
  {
    id: RolesEnum.psychologist,
    label: "Psychologist",
    tooltip:
      "Psychologists have access to evaluations and diagnosis tools across for clients they are assigned to, including the assessment template library filtered to their discipline.",
  },
  {
    id: RolesEnum["evaluation-psychologist"],
    label: "Evaluation Psychologist",
    tooltip: "Users that have access to all evaluation reports across all clients in an organization.",
  },
  {
    id: RolesEnum.intake,
    label: "Intake",
    tooltip:
      "Intake can onboard new clients, manage client records, and oversee provider assignments across the clinic. They do not have access to clinical tools or assessments.",
  },
];

type UserFormValues = Omit<CreateOrganizationUserInput, "organization_id" | "roles" | keyof ProviderProfileValues> &
  ProviderProfileValues;

interface AddEditUserProps {
  organization: GetOrganizationsOutput;
  user?: GetOrganizationUsersOutput;
  restoreTarget?: GetOrphanedOrganizationUsersOutput;
  onCancel: () => void;
  onSuccess?: () => void;
}

const splitFullName = (fullName: string | null): { first: string; last: string } => {
  if (!fullName) return { first: "", last: "" };
  const idx = fullName.indexOf(" ");
  if (idx === -1) return { first: fullName, last: "" };
  return { first: fullName.slice(0, idx), last: fullName.slice(idx + 1) };
};

export const AddEditUser: React.FC<AddEditUserProps> = ({ organization, user, restoreTarget, onCancel, onSuccess }) => {
  const isEditMode = !!user;
  const isRestoreMode = !!restoreTarget;
  const mode = getFormMode(isEditMode, isRestoreMode);
  const currentRoles = useBasecampRoles();
  const canAssignEnterpriseAdmin = currentRoles.includes(BasecampRole.ENTERPRISE_ADMIN);
  const { first, last } = splitFullName(user?.full_name ?? restoreTarget?.full_name ?? null);
  const isGoogleConnected = user?.identities.some((id) => id.toLowerCase().includes("google")) ?? false;

  const [selectedRoles, setSelectedRoles] = useState<Set<RolesEnum>>(
    new Set(user?.roles.map((r) => r.name as RolesEnum) ?? []),
  );
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);

  const { mutateAsync: createUser } = useBasecampOrganizationUsersCreateCreate();
  const { mutateAsync: updateUser } = useBasecampOrganizationUsersUpdateCreate();
  const { mutateAsync: restoreUser } = useBasecampOrganizationUsersRestoreCreate();
  const { mutateAsync: removeUser, isPending: isRemoving } = useBasecampOrganizationUsersRemoveCreate();

  // Baseline for change detection: what the page loaded with, advanced only by this form's own saves.
  // Not re-derived from a refetched `user`, so a provider's concurrent edit is never sent back as a change.
  const [originalProfile, setOriginalProfile] = useState<ProviderProfileValues>({
    professional_title: user?.professional_title ?? "",
    license_number: user?.license_number ?? "",
  });
  // Restore only sends roles, and caregivers have no ProviderMetadata to hold these fields.
  const showProfileFields = isEditMode ? user.fdm_provider : !isRestoreMode && !selectedRoles.has(RolesEnum.caregiver);

  const formUtils = useFormUtils<UserFormValues>({
    defaultValues: {
      first_name: first,
      last_name: last,
      email: user?.email ?? restoreTarget?.email ?? "",
      connection_type: undefined,
      ...originalProfile,
    },
    mutationFn: async (data) => {
      const roles = Array.from(selectedRoles);
      if (roles.length === 0) {
        notifyError("You must select at least one role");
        return;
      }

      let response;
      // Untouched profile fields are dropped rather than sent as "", which the API treats as "clear".
      const profileChanges = showProfileFields ? changedProviderProfileFields(data, originalProfile) : {};

      if (isEditMode) {
        const payload: UpdateOrganizationUserInput = {
          auth0_id: user.auth0_id!,
          organization_id: organization.id,
          first_name: data.first_name,
          last_name: data.last_name,
          roles,
          ...profileChanges,
        };
        response = await updateUser({ data: payload });
      } else if (isRestoreMode) {
        const payload: RestoreOrphanedOrganizationUserInput = {
          organization_id: organization.id,
          provider_id: restoreTarget.provider_id,
          roles,
        };
        response = await restoreUser({ data: payload });
      } else {
        const payload: CreateOrganizationUserInput = {
          email: data.email,
          first_name: data.first_name,
          last_name: data.last_name,
          connection_type: data.connection_type,
          ...profileChanges,
          organization_id: organization.id,
          roles,
        };
        response = await createUser({ data: payload });
      }

      if (response.status !== 200) {
        notifyError(
          (response.data && "detail" in response.data ? response.data.detail : null) || MODE_MESSAGES[mode].failure,
        );
        return;
      }

      notifySuccess(MODE_MESSAGES[mode].success);
      if (isEditMode) setOriginalProfile((prev) => ({ ...prev, ...profileChanges }));
      onSuccess?.();
      if (!isEditMode) onCancel();
    },
  });
  const { onSubmit, RegisteredFormInput, RegisteredFormSelected, formState, register, control, setValue } = formUtils;

  const toggleRole = (id: RolesEnum) => {
    setSelectedRoles((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleRemove = async () => {
    if (!user) return;

    const payload: RemoveOrganizationUserInput = {
      auth0_id: user.auth0_id!,
      organization_id: organization.id,
    };
    const response = await removeUser({ data: payload });

    if (response.status !== 200) {
      notifyError(
        (response.data && "detail" in response.data ? response.data.detail : null) || "Failed to remove user",
      );
      return;
    }

    notifySuccess("User removed from organization");
    setRemoveDialogOpen(false);
    onSuccess?.();
    onCancel();
  };

  const title = getTitle(mode, user, restoreTarget);

  return (
    <div className="flex flex-col min-h-full py-8 items-center bg-bg-default px-4 sm:px-6 lg:px-8">
      {/* Page header */}
      <div className="w-full max-w-[900px] mb-4">
        <div className="flex items-center gap-2 mb-2">
          <button
            type="button"
            onClick={onCancel}
            className="text-sm text-text-secondary hover:text-text-primary transition-colors"
          >
            Organizations
          </button>
          {organization.name && (
            <>
              <ChevronRightIcon className="h-3 w-3 text-text-tertiary" />
              <button
                type="button"
                onClick={onCancel}
                className="text-sm text-text-secondary hover:text-text-primary transition-colors"
              >
                {organization.name}
              </button>
            </>
          )}
          <ChevronRightIcon className="h-3 w-3 text-text-tertiary" />
          <span className="text-sm text-text-secondary">{title}</span>
        </div>
        <Heading size="medium">{title}</Heading>
      </div>

      {/* Form card */}
      <form onSubmit={onSubmit} className="w-full max-w-[900px]">
        <div className="bg-bg-surface border border-border-strong rounded-2xl px-4 sm:px-8 pt-6 pb-8 flex flex-col gap-8">
          {/* User details */}
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <Paragraph displayType="loud" size="small" colorType="primary">
                User details
              </Paragraph>
              {isGoogleConnected && (
                <Paragraph size="small" className="text-text-error-primary!">
                  Some fields cannot be updated for Google connected accounts
                </Paragraph>
              )}
            </div>

            {isRestoreMode ? (
              // Restore only ever sends organization_id/provider_id/roles — name and email
              // aren't editable or submitted here, so render them read-only rather than as
              // required form inputs (which would block submission on e.g. a single-word
              // full_name with no last name).
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Paragraph size="small" colorType="secondary">
                    Name
                  </Paragraph>
                  <Paragraph size="small">{restoreTarget?.full_name ?? "—"}</Paragraph>
                </div>
                <div>
                  <Paragraph size="small" colorType="secondary">
                    Email Address
                  </Paragraph>
                  <Paragraph size="small">{restoreTarget?.email ?? "—"}</Paragraph>
                </div>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <RegisteredFormInput formKey="first_name" label="First Name" disabled={isGoogleConnected} />
                  <RegisteredFormInput formKey="last_name" label="Last Name" disabled={isGoogleConnected} />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <RegisteredFormInput formKey="email" label="Email Address" type="email" disabled={isEditMode} />
                  {!isEditMode && (
                    <RegisteredFormSelected
                      formKey="connection_type"
                      title="Connection Type"
                      placeholderText="Select connection type..."
                      items={CONNECTION_TYPE_ITEMS}
                    />
                  )}
                </div>
              </>
            )}
          </div>

          {showProfileFields && (
            <ProviderProfileFields<UserFormValues>
              register={register}
              formState={formState}
              control={control}
              setValue={setValue}
              original={originalProfile}
              highlightChanges={isEditMode}
            />
          )}

          {/* Roles */}
          <div className="flex flex-col gap-3">
            <Paragraph displayType="loud" size="small" colorType="primary">
              Roles
            </Paragraph>

            <div className="flex flex-col gap-2">
              {ROLES.map(({ id, label, tooltip }) => (
                <div key={id} className="flex items-center gap-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedRoles.has(id)}
                      onChange={() => toggleRole(id)}
                      className="h-4 w-4 rounded border-border-strong accent-interactive-primary cursor-pointer"
                    />
                    <Paragraph size="small" colorType="primary">
                      {label}
                    </Paragraph>
                  </label>
                  <Tooltip
                    text={tooltip}
                    placement="right"
                    showInfoIcon
                    infoIconClassName="h-4 w-4 text-text-secondary"
                  />
                </div>
              ))}
              {canAssignEnterpriseAdmin && (
                <div className="flex items-center gap-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedRoles.has(RolesEnum["enterprise-admin"])}
                      onChange={() => toggleRole(RolesEnum["enterprise-admin"])}
                      className="h-4 w-4 rounded border-border-strong accent-interactive-primary cursor-pointer"
                    />
                    <Paragraph size="small" colorType="primary">
                      Enterprise Admin
                    </Paragraph>
                  </label>
                  <Tooltip
                    text="Enterprise admins have elevated access to template management and enterprise-level features across the portal."
                    placement="right"
                    showInfoIcon
                    infoIconClassName="h-4 w-4 text-text-secondary"
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex flex-col-reverse sm:flex-row sm:justify-between gap-3 pt-4">
          {isEditMode ? (
            <Button
              type="button"
              variant="negative"
              buttonStyle="border"
              size="md"
              text="Remove from organization"
              onClick={() => setRemoveDialogOpen(true)}
              disabled={formState.isSubmitting}
            />
          ) : (
            <div />
          )}
          <div className="flex flex-col-reverse sm:flex-row gap-3">
            <Button
              type="button"
              variant="primary"
              buttonStyle="border"
              size="md"
              text="Cancel"
              onClick={onCancel}
              disabled={formState.isSubmitting}
            />
            <Button
              type="submit"
              variant="primary"
              buttonStyle="filled"
              size="md"
              text={formState.isSubmitting ? SUBMIT_LABELS[mode].submitting : SUBMIT_LABELS[mode].idle}
              disabled={formState.isSubmitting}
            />
          </div>
        </div>
      </form>

      {isEditMode && (
        <Dialog
          title="Remove user from organization?"
          open={removeDialogOpen}
          setOpen={setRemoveDialogOpen}
          primaryButton={{
            text: "Remove",
            variant: "negative",
            onClick: handleRemove,
            disabled: isRemoving,
          }}
          secondaryButton={{
            text: "Cancel",
            onClick: () => setRemoveDialogOpen(false),
            disabled: isRemoving,
          }}
          size="sm"
        >
          <p className="text-sm text-text-secondary">
            This revokes {user.full_name ?? "this user"}&apos;s Auth0 organization membership only. Their
            provider/caregiver records are not deleted and can be restored later from the Orphaned Providers tab.
          </p>
        </Dialog>
      )}
    </div>
  );
};
